<template>
  <div class="flex flex-col gap-1.5">
    <div v-if="label" class="text-label text-text-muted">{{ label }}</div>

    <FloatingPopover
      v-model="opened"
      class="block w-full"
      align="start"
      :getBounds="visibleViewport"
      :trackOnScroll="!IS_IOS"
    >
      <div
        ref="fieldRef"
        class="field-shell group w-full gap-1.5 px-3"
        :class="[
          size === 'sm' ? 'min-h-8' : 'min-h-10 py-1',
          disabled ? 'is-disabled' : readonly ? 'is-readonly' : 'cursor-text hover:border-accent',
          opened ? 'border-accent' : '',
        ]"
        @mousedown="$event.target !== inputRef && $event.preventDefault()"
        @click="readonly || inputRef.focus()"
      >
        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <slot name="selected" :items="selectedItems" :remove="remove" :readonly="readonly">
            <template v-if="chip">
              <span
                v-for="item in selectedItems"
                :key="getValue(item)"
                class="flex h-5.5 max-w-full items-center gap-1 rounded-chip bg-hover px-2 text-label text-text"
              >
                <span class="truncate">{{ getLabel(item) }}</span>
                <IconX
                  v-if="!readonly"
                  class="size-3 flex-none cursor-pointer text-text-muted transition-colors
                         duration-150 hover:text-accent"
                  @click.stop="remove(getValue(item))"
                />
              </span>
            </template>
            <span v-else-if="selectedItems.length" class="min-w-0 truncate text-text">
              {{ selectedItems.map(getLabel).join(', ') }}
            </span>
          </slot>

          <span v-if="readonly && !selectedItems.length" class="text-text-muted">—</span>
          <input
            v-if="!readonly"
            ref="inputRef"
            type="text"
            name="combobox"
            :value="query"
            :disabled="disabled"
            :placeholder="selectedItems.length ? '' : placeholder"
            class="field-input"
            :class="focused || !selectedItems.length ? 'min-w-16 flex-1' : 'absolute size-0 opacity-0'"
            @focus="onFocus"
            @blur="onBlurDebug"
            @input="onInput($event.target.value)"
            @keydown.up.prevent="move('selectPrev')"
            @keydown.down.prevent="move('selectNext')"
            @keydown.enter.prevent="onEnter"
            @keydown.backspace="removeLast"
          >
        </div>

        <span
          v-if="clearable && !readonly && selectedItems.length"
          class="flex size-4 flex-none cursor-pointer items-center justify-center text-text-muted
                 transition-colors duration-150 hover:text-accent"
          :title="t('combobox.clear')"
          @click.stop="clear"
        >
          <IconX class="size-3.5" />
        </span>
        <IconLockClosed16Solid v-if="readonly" class="size-4 shrink-0 self-center text-text-muted" />
        <IconChevronDown
          v-else
          class="pointer-events-none size-4 shrink-0 self-center transition-[rotate,color]
                 duration-300 ease-out group-hover:text-accent"
          :class="opened ? 'rotate-180 text-accent' : 'text-text-muted'"
        />
      </div>

      <template #content>
        <div
          class="popover-panel p-2"
          @mousedown.prevent
          :class="panelClass"
          :style="{ width: `${fieldWidth}px` }"
        >
          <AppItemsList
            v-show="filtered.length"
            ref="listRef"
            :items="filtered"
            :getLabel="getLabel"
            :closeOnSelect="!multiple"
            @select="pick"
          >
            <template v-if="$slots.item" #item="slotProps">
              <slot name="item" v-bind="slotProps" />
            </template>
          </AppItemsList>

          <slot v-if="!filtered.length" name="empty" :query="query.trim()">
            <div class="flex min-h-7.5 items-center px-2.5 text-label text-text-muted">
              <template v-if="addingAllowed && query.trim()">
                {{ t('combobox.create_hint', { query: query.trim() }) }}
              </template>
              <template v-else>{{ t('combobox.nothing_found') }}</template>
            </div>
          </slot>

          <!-- below the suggestions, so a host can offer an action on the query itself -->
          <slot name="footer" :query="query.trim()" />
        </div>
      </template>
    </FloatingPopover>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconX } from '@iconify-prerendered/vue-tabler';
import { IconChevronDown, IconLockClosed16Solid } from '@iconify-prerendered/vue-heroicons';
import FloatingPopover from '@/components/elements/FloatingPopover.vue';
import AppItemsList from '@/components/elements/AppItemsList';
import { IS_IOS, visibleViewport } from '@/composables/useFloatingPosition';
import { filterItems } from '@/utils';

const props = defineProps({
  // array of values when `multiple`, a single value otherwise
  modelValue: {
    type: [Array, String, Number],
    default: null,
  },
  items: {
    type: Array,
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  chip: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
  },
  getLabel: {
    type: Function,
    default: item => item.label,
  },
  getValue: {
    type: Function,
    default: item => item.id,
  },
  // text the query is matched against, defaults to the label
  getSearchText: {
    type: Function,
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  addingAllowed: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // shows the picked values as they are: never opens, nothing to remove
  readonly: {
    type: Boolean,
    default: false,
  },
  // the panel is teleported, so a host's click-outside guard needs a class to recognise it
  panelClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'create']);

const { t } = useI18n();

const opened = ref(false);
// with chips picked, the input only takes room while typing, else it wraps onto an empty row.
// The panel is open only while the input has focus (clicks inside it keep the focus), so the
// field never looks active with the input collapsed
const focused = ref(false);
const query = ref('');
const fieldWidth = ref(0);
const fieldRef = ref(null);
const inputRef = ref(null);
const listRef = ref(null);

const close = () => {
  opened.value = false;
};

// AppItemsList closes its host panel through this on select
provide('dropdownClose', close);

const selectedValues = computed(() => {
  if (props.multiple) return props.modelValue || [];
  return props.modelValue === null ? [] : [props.modelValue];
});

const selectedItems = computed(
  () => props.items.filter(item => selectedValues.value.includes(props.getValue(item))),
);

// picked entries live in the field as chips, so they drop out of the list
const filtered = computed(() => filterItems(
  props.items.filter(item => !selectedValues.value.includes(props.getValue(item))),
  query.value,
  props.getSearchText || props.getLabel,
));

watch(opened, (isOpen) => {
  // TEMP debug: dropdown flicker on selection
  console.log('[popover-debug] combobox opened ->', isOpen);
  if (isOpen) {
    fieldWidth.value = fieldRef.value.offsetWidth;
    return;
  }
  query.value = '';
});

function onFocus() {
  // TEMP debug: dropdown flicker on selection
  console.log('[popover-debug] combobox focus, opened:', opened.value);
  focused.value = true;
  opened.value = true;
}

// TEMP debug: dropdown flicker on selection
function onBlurDebug(e) {
  console.log('[popover-debug] combobox blur -> input collapses, opened:', opened.value, 'focus goes to:', e.relatedTarget);
  focused.value = false;
  close();
}

// the list is mounted with the panel, so its cursor only needs resetting while open
function onInput(value) {
  query.value = value;
  if (opened.value) listRef.value.resetSelection();
  else opened.value = true;
}

function move(method) {
  if (!opened.value) {
    opened.value = true;
    return;
  }
  listRef.value[method]();
}

function settle() {
  query.value = '';
  if (props.multiple) {
    inputRef.value.focus();
    return;
  }
  close();
  inputRef.value.blur();
}

function pick(item) {
  const value = props.getValue(item);
  // TEMP debug: dropdown flicker on selection
  console.log('[popover-debug] combobox pick', value, 'opened:', opened.value, 'focused:', focused.value);
  emit('update:modelValue', props.multiple ? [...selectedValues.value, value] : value);
  settle();
}

function remove(value) {
  emit('update:modelValue', props.multiple ? selectedValues.value.filter(v => v !== value) : null);
}

function clear() {
  emit('update:modelValue', props.multiple ? [] : null);
}

// selectedValues keeps the order things were picked in, unlike selectedItems
function removeLast() {
  if (query.value || !selectedValues.value.length) return;
  remove(selectedValues.value[selectedValues.value.length - 1]);
}

function onEnter() {
  if (opened.value && filtered.value.length) {
    listRef.value.selectByKeyboard();
    return;
  }
  const trimmed = query.value.trim();
  if (!props.addingAllowed || !trimmed) return;
  emit('create', trimmed);
  settle();
}

// `create` is handled by the host, which calls this back once the item exists so the
// field finishes the same way it does after a pick. Needed because a host can offer
// creating from inside the panel (a button there takes the focus off the input).
defineExpose({ settle });
</script>
